import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Radio, Checkbox, Button, Input, Select, message, Alert, Space, Typography, Divider } from 'antd';
import { CopyOutlined, LinkOutlined } from '@ant-design/icons';
import { validateEmail, validatePhone, validateName, validateTaxpayerID, validateCEP } from '../../../../utils/validatesCheckout';
import { searchPostalCode } from '../../../../utils/searchPostalCode';

const { Option } = Select;
const { Text } = Typography;

const BRAZILIAN_STATES = [
  { code: 'AC', name: 'Acre' },
  { code: 'AL', name: 'Alagoas' },
  { code: 'AP', name: 'Amapá' },
  { code: 'AM', name: 'Amazonas' },
  { code: 'BA', name: 'Bahia' },
  { code: 'CE', name: 'Ceará' },
  { code: 'DF', name: 'Distrito Federal' },
  { code: 'ES', name: 'Espírito Santo' },
  { code: 'GO', name: 'Goiás' },
  { code: 'MA', name: 'Maranhão' },
  { code: 'MT', name: 'Mato Grosso' },
  { code: 'MS', name: 'Mato Grosso do Sul' },
  { code: 'MG', name: 'Minas Gerais' },
  { code: 'PA', name: 'Pará' },
  { code: 'PB', name: 'Paraíba' },
  { code: 'PR', name: 'Paraná' },
  { code: 'PE', name: 'Pernambuco' },
  { code: 'PI', name: 'Piauí' },
  { code: 'RJ', name: 'Rio de Janeiro' },
  { code: 'RN', name: 'Rio Grande do Norte' },
  { code: 'RS', name: 'Rio Grande do Sul' },
  { code: 'RO', name: 'Rondônia' },
  { code: 'RR', name: 'Roraima' },
  { code: 'SC', name: 'Santa Catarina' },
  { code: 'SP', name: 'São Paulo' },
  { code: 'SE', name: 'Sergipe' },
  { code: 'TO', name: 'Tocantins' }
];

const QuickLinkModal = ({ visible, onClose, baseLink }) => {
  const [paymentType, setPaymentType] = useState('pix');
  const [autoCheckout, setAutoCheckout] = useState(true);
  const [generatedLink, setGeneratedLink] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    taxpayer: '',
    cep: '',
    address: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  });
  const [errors, setErrors] = useState({});
  const [postalCodeData, setPostalCodeData] = useState(null);

  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (field === 'cep' && value.replace(/\D/g, '').length === 8) {
      setPostalCodeData(null);
      searchPostalCode(value, setPostalCodeData);
    }
  }, [errors]);

  useEffect(() => {
    if (postalCodeData && !postalCodeData?.erro) {
      setFormData(prev => ({
        ...prev,
        address: postalCodeData.logradouro || prev.address,
        neighborhood: postalCodeData.bairro || prev.neighborhood,
        city: postalCodeData.localidade || prev.city,
        state: postalCodeData.uf || prev.state,
        cep: postalCodeData.cep || prev.cep,
      }));
      setErrors(prev => ({
        ...prev,
        address: '',
        neighborhood: '',
        city: '',
        state: '',
      }));
    }
  }, [postalCodeData]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    const nameError = validateName(formData.name);
    if (nameError) newErrors.name = nameError;

    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    const phoneError = validatePhone(formData.phone.replace(/\D/g, ''));
    if (phoneError) newErrors.phone = phoneError;

    if (paymentType === 'boleto') {
      const taxpayerError = validateTaxpayerID(formData.taxpayer);
      if (taxpayerError) newErrors.taxpayer = taxpayerError;

      const cepError = validateCEP(formData.cep.replace(/\D/g, ''));
      if (cepError) newErrors.cep = cepError;

      if (!formData.address.trim()) newErrors.address = 'Esse campo é obrigatório';
      if (!formData.number.trim()) newErrors.number = 'Esse campo é obrigatório';
      if (!formData.neighborhood.trim()) newErrors.neighborhood = 'Esse campo é obrigatório';
      if (!formData.city.trim()) newErrors.city = 'Esse campo é obrigatório';
      if (!formData.state) newErrors.state = 'Esse campo é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, paymentType]);

  const generateLink = useCallback(() => {
    if (!validateForm()) {
      message.error('Por favor, corrija os erros no formulário');
      return;
    }

    const params = new URLSearchParams();

    params.set('name', formData.name);
    params.set('email', formData.email);
    params.set('phone', formData.phone.replace(/\D/g, ''));

    if (paymentType === 'boleto') {
      params.set('taxpayer', formData.taxpayer);
      params.set('cep', formData.cep.replace(/\D/g, ''));
      params.set('address', formData.address);
      params.set('number', formData.number);
      if (formData.complement) params.set('complement', formData.complement);
      params.set('neighborhood', formData.neighborhood);
      params.set('city', formData.city);
      params.set('state', formData.state);
    }

    if (autoCheckout) {
      params.set('autoCheckout', paymentType);
    }

    const finalLink = `${baseLink}?${params.toString()}`;
    setGeneratedLink(finalLink);
  }, [formData, paymentType, autoCheckout, baseLink, validateForm]);

  const copyLink = useCallback(() => {
    navigator.clipboard.writeText(generatedLink);
    message.success('Link copiado para a área de transferência!');
  }, [generatedLink]);

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      taxpayer: '',
      cep: '',
      address: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: ''
    });
    setErrors({});
    setGeneratedLink('');
    setPaymentType('pix');
    setAutoCheckout(true);
    setPostalCodeData(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const renderInput = (field, label, placeholder, maxLength) => (
    <div style={{ marginBottom: 16 }}>
      <Text strong style={{ display: 'block', marginBottom: 4 }}>{label}</Text>
      <Input
        value={formData[field]}
        onChange={(e) => handleChange(field, e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        status={errors[field] ? 'error' : ''}
      />
      {errors[field] && <Text type="danger" style={{ fontSize: 12 }}>{errors[field]}</Text>}
    </div>
  );

  return (
    <Modal
      title="Criar Link Rápido"
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={600}
    >
      <Alert
        message="Crie um link personalizado com os dados do cliente já preenchidos. Isso permite enviar um link pronto para pagamento, agilizando o processo de checkout."
        type="info"
        showIcon
        style={{ marginBottom: 20 }}
      />

      <div style={{ marginBottom: 20 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Tipo de Pagamento</Text>
        <Radio.Group
          value={paymentType}
          onChange={(e) => {
            setPaymentType(e.target.value);
            setGeneratedLink('');
          }}
        >
          <Radio.Button value="pix">PIX</Radio.Button>
          <Radio.Button value="boleto">Boleto</Radio.Button>
        </Radio.Group>
        <Alert
          message={<><strong>Nota:</strong> Por motivos de segurança, não é possível pré-preencher dados para pagamentos com cartão de crédito.</>}
          type="warning"
          style={{ marginTop: 12 }}
        />
      </div>

      <Divider>Dados do Cliente</Divider>

      {renderInput('name', 'Nome Completo', 'Ex: João da Silva')}

      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ flex: 1 }}>
          {renderInput('email', 'E-mail', 'Ex: joao@email.com')}
        </div>
        <div style={{ flex: 1 }}>
          {renderInput('phone', 'Telefone', 'Ex: 11999999999', 11)}
        </div>
      </div>

      {paymentType === 'boleto' && (
        <>
          <Divider>Dados para Boleto</Divider>

          {renderInput('taxpayer', 'CPF ou CNPJ', 'Apenas números', 14)}

          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1 }}>
              {renderInput('cep', 'CEP', 'Ex: 01310100', 9)}
            </div>
            <div style={{ flex: 2 }}>
              {renderInput('address', 'Endereço', 'Ex: Av. Paulista')}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1 }}>
              {renderInput('number', 'Número', 'Ex: 1000')}
            </div>
            <div style={{ flex: 1 }}>
              {renderInput('complement', 'Complemento', 'Ex: Sala 101')}
            </div>
            <div style={{ flex: 1 }}>
              {renderInput('neighborhood', 'Bairro', 'Ex: Bela Vista')}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1 }}>
              {renderInput('city', 'Cidade', 'Ex: São Paulo')}
            </div>
            <div style={{ flex: 1, marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>Estado</Text>
              <Select
                value={formData.state}
                onChange={(value) => handleChange('state', value)}
                placeholder="Selecione"
                status={errors.state ? 'error' : ''}
                style={{ width: '100%' }}
              >
                {BRAZILIAN_STATES.map(state => (
                  <Option key={state.code} value={state.code}>
                    {state.name}
                  </Option>
                ))}
              </Select>
              {errors.state && <Text type="danger" style={{ fontSize: 12 }}>{errors.state}</Text>}
            </div>
          </div>
        </>
      )}

      <div style={{ background: '#fafafa', padding: 16, borderRadius: 8, marginBottom: 20 }}>
        <Checkbox
          checked={autoCheckout}
          onChange={(e) => setAutoCheckout(e.target.checked)}
        >
          Iniciar checkout automático
        </Checkbox>
        <Text type="secondary" style={{ display: 'block', marginLeft: 24, fontSize: 12 }}>
          Quando habilitado, o pagamento será iniciado automaticamente ao abrir o link.
        </Text>
      </div>

      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <Button
          type="primary"
          onClick={generateLink}
          icon={<LinkOutlined />}
          size="large"
        >
          Gerar Link
        </Button>
      </div>

      {generatedLink && (
        <>
          <Alert
            message="Link Gerado"
            description={
              <Space.Compact style={{ width: '100%', marginTop: 8 }}>
                <Input value={generatedLink} readOnly />
                <Button type="primary" icon={<CopyOutlined />} onClick={copyLink}>
                  Copiar
                </Button>
              </Space.Compact>
            }
            type="success"
            showIcon
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
            Se o link gerado apresentar problemas, tente desabilitar a opção "Iniciar checkout automático".
          </Text>
        </>
      )}
    </Modal>
  );
};

export default QuickLinkModal;
